import { useCallback, useState } from 'react';
import Link from 'next/link';
import { OperationTab } from '@/types/common';
import { useFm } from '@web3/base-hooks';
import { Countdown, Modal } from '@bfe/moly';
import { formatNumber, appPath } from '@web3/base-utils';
import { NoSSR } from '@web3/base-components';
import { useWallet } from '@/hooks/wallet';
import { formatUSDYield } from '@/utils/num';
import BigNumber from 'bignumber.js';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { initialWindowType } from '@/hooks/media';
import { useStakingStore } from '@/store/staking';
import { useCurrentToken } from '@/containers/pool/operation/stake/hooks';
import { useAirdropReward } from '@/containers/operation/airdrop/Rewards';
import { AirdropActivity } from '@/types/bbsol';
import textStyles from '@/assets/css/text.module.scss';
import styles from './index.module.scss';

interface Props {
  onChangeTab: (v: OperationTab) => void;
  activity: AirdropActivity;
}

export const Airdrop = (props: Props) => {
  const fm = useFm();
  const { currentStakeToken } = useCurrentToken();
  const { activity, onChangeTab } = props;
  const { isLogin, solanaWalletInfo } = useWallet();
  //
  const { reward } = useAirdropReward({
    walletAddress: solanaWalletInfo?.address,
    activityCode: activity?.activityCode,
  });
  const { setAirdrop } = useStakingStore();
  const openModal = () => setAirdrop({ isRewardsOpened: true });
  //
  const { isMobile } = initialWindowType;
  //
  const onButtonClick = useCallback(() => {
    if (!isLogin) {
      onChangeTab(OperationTab.Stake);
      return;
    }
    openModal();
    //
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPage',
      button_name: isLogin ? 'Airdrop_myself' : 'Airdrop_login',
    });
  }, [isLogin]);
  //
  let countdownTime = new Date().getTime();
  if (activity?.status === 'ongoing') {
    countdownTime = activity?.endTime;
  }
  if (activity?.status === 'upcoming') {
    countdownTime = activity?.startTime;
  }
  //
  return (
    <>
      <div className="absolute top-7 right-6">
        <Link
          className="text-xs hover:text-white cursor-pointer bg-bds-gradient rounded-full py-1 px-2 text-bds-gray-secondary"
          target="_blank"
          href={isMobile ? activity?.jumpLinkApp : activity.jumpLinkWeb}
          onClick={() => {
            scTrack(Event.Web3UserClick, {
              page_name: 'StakingWeb_bbSOLPage',
              button_name: 'Airdrop_detail',
            });
          }}
        >
          {fm('airdropRule')}
        </Link>
      </div>
      <div className="flex flex-col">
        <div className="flex-1 my-8">
          <div className="py-1 flex justify-between">
            <div className="flex items-center gap-x-3">
              <div className="h-12 w-12 rounded-full overflow-hidden">
                <img
                  src={`${appPath}/png/bybitsol/bbsolIcon.png`}
                  className="h-full w-full"
                  alt="bbSOL"
                />
              </div>
              <div className={`${styles['text-3xl']} font-semibold leading-10`}>
                bbSOL
              </div>
            </div>

            <div>
              <div
                className={`${textStyles['gradient-text']} ${styles['text-3xl']} font-semibold gradient-text leading-10`}
              >
                {isLogin
                  ? formatNumber(reward?.snapshotAmount || '0', {
                      kmb: true,
                      decimal: 4,
                      roundMode: 1,
                      smallNumberUseValidDigits: false,
                      collapseZero: false,
                    })
                  : '--'}
              </div>
              <div className="text-xs text-bds-gray-secondary text-right">
                {isLogin
                  ? formatNumber(
                      BigNumber(reward?.snapshotAmount || '0')
                        .times(currentStakeToken?.price || 0)
                        .toString(),
                      {
                        kmb: false,
                        decimal: 2,
                        roundMode: 1,
                        smallNumberUseValidDigits: false,
                        collapseZero: false,
                        prefix: '$',
                      }
                    ).replace(/,/g, '')
                  : '--'}
              </div>
            </div>
          </div>
          <hr className="border-bds-divide my-6" />
          <div className="py-1 flex justify-between">
            <div className="flex items-center gap-x-3">
              <div className="h-12 w-12 rounded-full overflow-hidden">
                <img
                  src={activity?.rewardTokenDetail?.iconNightUrl}
                  className="h-full w-full"
                  alt={activity?.rewardTokenDetail?.symbol}
                />
              </div>
              <div
                className={`${styles['text-3xl']} text-bds-3xl font-semibold leading-10`}
              >
                {activity?.rewardTokenDetail?.symbol || '--'}
              </div>
            </div>

            <div className="text-right">
              <div
                className={`${textStyles['gradient-text']} ${styles['text-3xl']} font-semibold gradient-text leading-10`}
              >
                {isLogin
                  ? formatNumber(reward?.estimatedTotalRewards || '0', {
                      kmb: true,
                      decimal: 1,
                      roundMode: 1,
                      smallNumberUseValidDigits: false,
                      collapseZero: false,
                    })
                  : '--'}
              </div>
              <div className="text-xs text-bds-gray-secondary">
                {isLogin
                  ? formatNumber(
                      BigNumber(reward?.estimatedTotalRewards || '0')
                        .times(activity?.rewardTokenDetail?.price || 0)
                        .toString(),
                      {
                        kmb: false,
                        decimal: 2,
                        roundMode: 1,
                        smallNumberUseValidDigits: false,
                        collapseZero: false,
                        prefix: '$',
                      }
                    ).replace(/,/g, '')
                  : '--'}
              </div>
            </div>
          </div>
        </div>

        {['upcoming'].includes(activity?.status) && (
          <div className="h-12 mt-auto flex items-center justify-between">
            <div className="text-xs text-bds-gray-secondary">
              {fm('eventStartsOn')}
            </div>
            <div>
              <NoSSR>
                <Countdown
                  targetTimestamp={countdownTime}
                  size="small"
                  mode="easy"
                />
              </NoSSR>
            </div>
          </div>
        )}

        {['ongoing'].includes(activity?.status) && (
          <div className="flex mt-auto justify-between items-center">
            <div className="h-12 flex flex-col justify-between">
              <div className="text-xs text-bds-gray-secondary">
                {fm('campaignEndsIn')}
              </div>
              <div className="flex">
                <NoSSR>
                  <Countdown
                    targetTimestamp={countdownTime}
                    size="small"
                    mode="easy"
                  />
                </NoSSR>
              </div>
            </div>
            <button
              type="button"
              onClick={onButtonClick}
              className="py-2 px-6 min-w-28 rounded-md text-base font-bold hover:bg-bds-brand-500 bg-bds-brand-primary h-12 flex items-center justify-center"
            >
              {isLogin ? fm('myAirdrop') : fm('loginForAirdrop')}
            </button>
          </div>
        )}
        {activity?.status === 'ended' && (
          <div
            onClick={onButtonClick}
            className="w-full border rounded-lg font-semibold h-12 flex items-center justify-center text-bds-gray-secondary border-bds-divide py-4"
          >
            <span>{fm('airdropEnded')}</span>
          </div>
        )}
      </div>
    </>
  );
};
